/**
 * Chasing a membership claim that has not arrived yet.
 *
 * A first-ever sign-in can arrive before `syncMembershipClaims` has written the
 * `events` claim, so the token in hand is stale and one forced refresh is worth
 * doing. But a forced refresh issues a new token, which fires
 * `onIdTokenChanged`, which re-enters the handler with the claim *still* empty
 * — so refreshing unconditionally on an empty claim is an infinite loop. It was
 * one: BUG-1, seen on a real phone on 27 Aug 2026, and Firebase rate-limits
 * sustained forced refreshes, which is a plausible route from "spinning" to
 * "signed out".
 *
 * So the chase is bounded and backed off. Three attempts over about fifteen
 * seconds covers a cold start of the function; after that a user who is
 * genuinely on no event sits still in `noEvent` rather than hammering the token
 * endpoint. They are told to ask the organiser and reload, and reloading gives
 * a fresh budget.
 *
 * The budget is per uid, so signing out and in as someone else starts again.
 */

/** Waits between attempts. Its length is the number of attempts. */
const DELAYS_MS: readonly number[] = [1_500, 4_000, 9_000];

/** Cancels a scheduled refresh. */
type Cancel = () => void;

export interface ClaimChaseOptions {
  /** Forces a token refresh. Not awaited — the new token arrives as an event. */
  readonly refresh: () => void;
  /** Injected so tests need neither timers nor a clock. */
  readonly schedule?: (run: () => void, afterMs: number) => Cancel;
  /** Injected so a test can shorten or lengthen the chase. */
  readonly delaysMs?: readonly number[];
}

export interface ClaimChase {
  /** A session arrived with no event claim. Schedules a refresh, if any remain. */
  readonly missing: (uid: string) => void;
  /** A claim arrived, or nobody is signed in. Cancels and restores the budget. */
  readonly reset: () => void;
  /** Teardown. Cancels anything pending and refuses further work. */
  readonly stop: () => void;
  /** Attempts used against the current uid. For tests and diagnosis. */
  readonly attempts: () => number;
}

function defaultSchedule(run: () => void, afterMs: number): Cancel {
  const id = setTimeout(run, afterMs);
  return () => clearTimeout(id);
}

export function createClaimChase(options: ClaimChaseOptions): ClaimChase {
  const schedule = options.schedule ?? defaultSchedule;
  const delays = options.delaysMs ?? DELAYS_MS;

  let uid: string | null = null;
  let attempts = 0;
  let cancel: Cancel | undefined;
  let stopped = false;

  function clear(): void {
    cancel?.();
    cancel = undefined;
  }

  return {
    missing(next: string): void {
      if (stopped) return;

      // A different person: their own budget, and nothing owed to the last one.
      if (next !== uid) {
        clear();
        uid = next;
        attempts = 0;
      }

      // One in flight is enough. Without this the re-entrant call that the
      // refresh itself provokes would schedule a second chase alongside the
      // first, and the bound would only slow the loop down rather than end it.
      if (cancel !== undefined) return;

      const delay = delays[attempts];
      if (delay === undefined) return; // Budget spent. Stay in noEvent.

      attempts += 1;
      cancel = schedule(() => {
        cancel = undefined;
        if (stopped) return;
        options.refresh();
      }, delay);
    },

    reset(): void {
      clear();
      uid = null;
      attempts = 0;
    },

    stop(): void {
      stopped = true;
      clear();
    },

    attempts(): number {
      return attempts;
    },
  };
}
